import { CartAddEvent } from '@theme/events';
import {
  appendCartRecommendationFallbacks,
  createCartRecommendationSizeConfig,
  getCartRecommendationSeeds,
  getCartRecommendationInitialState,
  getCartRecommendationSizeLabel,
  rankCartRecommendations,
} from '@theme/cart-recommendations-logic';

/** @type {Map<string, string>} */
const recommendationHtmlCache = new Map();
const CART_REC_INIT_FLAG = '__mmCartRecommendationsInitialized';
const CART_REC_OBSERVER_KEY = '__mmCartRecommendationsObserver';
const MAX_RECOMMENDATION_SEEDS = 5;
const MAX_RECOMMENDATION_PRODUCTS = 3;
const MAX_CACHE_ENTRIES = 12;

function getShopifyRoot() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function parseCartRecommendationContext(container) {
  const contextElement = container.querySelector('[data-cart-recommendation-context]');
  if (!contextElement?.textContent) return null;

  try {
    return JSON.parse(contextElement.textContent);
  } catch (error) {
    console.error('Failed to parse cart recommendation context:', error);
    return null;
  }
}

function getCartRecommendationState(container) {
  const context = parseCartRecommendationContext(container);
  if (!context) return null;

  const sizeConfig = createCartRecommendationSizeConfig(context.sizeConfig);
  const items = Array.isArray(context.items) ? context.items : [];
  const paidProducts = getCartRecommendationSeeds(items, items.length, sizeConfig);
  const seeds = paidProducts.slice(0, MAX_RECOMMENDATION_SEEDS);
  const desiredSizes = [...new Set(paidProducts.flatMap((product) => product.sizes))];
  const cartProductIds = [...new Set(items.map((item) => String(item.productId || '')).filter(Boolean))];
  const fallbackHandles = [
    ...new Set(
      (Array.isArray(context.fallbackHandles) ? context.fallbackHandles : [])
        .map((handle) => String(handle || '').trim())
        .filter(Boolean)
    ),
  ];
  const contextKey = JSON.stringify({
    cartProductIds,
    desiredSizes,
    fallbackHandles,
    seeds,
    sizeConfig: context.sizeConfig || {},
  });

  return { cartProductIds, contextKey, desiredSizes, fallbackHandles, seeds, sizeConfig };
}

function cacheRecommendationMarkup(contextKey, markup) {
  recommendationHtmlCache.set(contextKey, markup);
  if (recommendationHtmlCache.size <= MAX_CACHE_ENTRIES) return;

  const oldestKey = recommendationHtmlCache.keys().next().value;
  recommendationHtmlCache.delete(oldestKey);
}

function markRecommendationContainerLoaded(container, contextKey, markup) {
  const list = container.querySelector('#cart-recommendations-list');
  if (!list) return;

  list.innerHTML = markup;
  container.dataset.loaded = 'true';
  container.dataset.loadedForContext = contextKey;
  delete container.dataset.loadingForContext;
  container.style.display = markup ? '' : 'none';
}

function renderRecommendationCard(product) {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const initialState = getCartRecommendationInitialState(variants);
  const variantPills = variants
    .map((variant) => {
      const sizeLabel = getCartRecommendationSizeLabel(variant.title, product, variant);
      return `<button type="button"
        class="cart-rec-pill${!variant.available ? ' cart-rec-pill--sold-out' : ''}"
        data-variant-id="${escapeHtml(variant.id)}"
        aria-pressed="false"
        ${!variant.available ? 'disabled' : ''}
        >${escapeHtml(sizeLabel)}</button>`;
    })
    .join('');

  const price = Number(product.price) || 0;
  const compareAtPrice = Number(product.compare_at_price) || 0;
  const priceHtml =
    compareAtPrice > price
      ? `<s>$${(compareAtPrice / 100).toFixed(2)}</s> <strong>$${(price / 100).toFixed(2)}</strong>`
      : `<strong>$${(price / 100).toFixed(2)}</strong>`;
  const imageUrl = product.featured_image
    ? product.featured_image.replace(/\.(jpe?g|png|gif|webp)/i, '_200x.$1')
    : '';

  return `
    <div class="cart-rec-card">
      <div class="cart-rec-card__image">
        <img src="${escapeHtml(imageUrl)}"
             alt="${escapeHtml(product.title)}" loading="lazy">
      </div>
      <div class="cart-rec-card__info">
        <p class="cart-rec-card__title">${escapeHtml(product.title)}</p>
        <p class="cart-rec-card__price">${priceHtml}</p>
        <div class="cart-rec-card__actions">
          ${variants.length > 1 ? `<div class="cart-rec-card__variants">${variantPills}</div>` : ''}
          <button
            type="button"
            class="cart-rec-card__add button"
            data-variant-id="${escapeHtml(initialState.variantId)}"
            onclick="addRecommendationToCart(this)"
            ${initialState.disabled ? 'disabled' : ''}
          >${initialState.label}</button>
        </div>
      </div>
    </div>
  `;
}

async function fetchSeedRecommendations(seed) {
  const url =
    `${getShopifyRoot()}recommendations/products.json?product_id=` +
    `${encodeURIComponent(seed.productId)}&limit=10&intent=related`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Recommendation request failed with status ${response.status}`);
  }

  const data = await response.json();
  return {
    seed,
    products: Array.isArray(data.products) ? data.products : [],
  };
}

async function fetchFallbackProducts(handles, productsNeeded) {
  const requestCount = Math.min(handles.length, Math.max(productsNeeded * 2, 3));
  const settledResults = await Promise.allSettled(
    handles.slice(0, requestCount).map(async (handle) => {
      const response = await fetch(
        `${getShopifyRoot()}products/${encodeURIComponent(handle)}.js`
      );
      if (!response.ok) {
        throw new Error(`Fallback product request failed with status ${response.status}`);
      }
      return response.json();
    })
  );

  return settledResults
    .filter((result) => result.status === 'fulfilled')
    .map((result) => result.value);
}

async function loadCartRecommendations(container) {
  if (!container?.isConnected) return;

  const state = getCartRecommendationState(container);
  if (!state) {
    container.style.display = 'none';
    return;
  }

  const {
    cartProductIds,
    contextKey,
    desiredSizes,
    fallbackHandles,
    seeds,
    sizeConfig,
  } = state;
  if (
    container.dataset.loadedForContext === contextKey ||
    container.dataset.loadingForContext === contextKey
  ) {
    return;
  }

  const cachedMarkup = recommendationHtmlCache.get(contextKey);
  if (cachedMarkup !== undefined) {
    markRecommendationContainerLoaded(container, contextKey, cachedMarkup);
    return;
  }

  if (seeds.length === 0) {
    cacheRecommendationMarkup(contextKey, '');
    markRecommendationContainerLoaded(container, contextKey, '');
    return;
  }

  container.dataset.loadingForContext = contextKey;
  container.style.display = 'none';

  try {
    const settledResults = await Promise.allSettled(seeds.map(fetchSeedRecommendations));
    const seedResults = settledResults
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value);

    if (seedResults.length === 0) {
      throw settledResults[0]?.reason || new Error('No recommendation requests succeeded');
    }

    const currentState = getCartRecommendationState(container);
    if (!container.isConnected || currentState?.contextKey !== contextKey) return;

    let products = rankCartRecommendations({
      cartProductIds,
      desiredSizes,
      seedResults,
      limit: MAX_RECOMMENDATION_PRODUCTS,
      sizeConfig,
    });
    if (products.length < MAX_RECOMMENDATION_PRODUCTS && fallbackHandles.length > 0) {
      const fallbackProducts = await fetchFallbackProducts(
        fallbackHandles,
        MAX_RECOMMENDATION_PRODUCTS - products.length
      );
      const latestState = getCartRecommendationState(container);
      if (!container.isConnected || latestState?.contextKey !== contextKey) return;

      products = appendCartRecommendationFallbacks({
        products,
        fallbackProducts,
        cartProductIds,
        desiredSizes,
        limit: MAX_RECOMMENDATION_PRODUCTS,
        sizeConfig,
      });
    }
    const markup = products.map(renderRecommendationCard).join('');

    cacheRecommendationMarkup(contextKey, markup);
    markRecommendationContainerLoaded(container, contextKey, markup);
  } catch (error) {
    console.error('Failed to load cart recommendations:', error);
    if (container.dataset.loadingForContext === contextKey) {
      markRecommendationContainerLoaded(container, contextKey, '');
    }
  } finally {
    if (container.dataset.loadingForContext === contextKey) {
      delete container.dataset.loadingForContext;
    }
  }
}

function handleCartRecommendationClick(e) {
  const pill = e.target.closest('.cart-rec-pill');
  if (pill && !pill.disabled) {
    const card = pill.closest('.cart-rec-card');
    card.querySelectorAll('.cart-rec-pill').forEach(p => {
      p.classList.remove('cart-rec-pill--active');
      p.setAttribute('aria-pressed', 'false');
    });
    pill.classList.add('cart-rec-pill--active');
    pill.setAttribute('aria-pressed', 'true');
    const addBtn = card.querySelector('.cart-rec-card__add');
    if (addBtn) {
      addBtn.dataset.variantId = pill.dataset.variantId;
      addBtn.disabled = false;
      addBtn.textContent = 'Add';
    }
  }
}

window.addRecommendationToCart = async function(button) {
  const card = button.closest('.cart-rec-card');
  const activePill = card.querySelector('.cart-rec-pill--active');
  const variantId = activePill ? activePill.dataset.variantId : button.dataset.variantId;
  if (!variantId) return;
  
  button.disabled = true;
  const originalText = button.textContent;
  button.textContent = '...';
  
  try {
    // Get section IDs for cart drawer update
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(el => {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const body = {
      id: parseInt(variantId),
      quantity: 1
    };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const res = await fetch(`${getShopifyRoot()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });

    const data = await res.json();

    if (data.status) {
      // Error
      console.error('Add to cart failed:', data.description || data.message);
      button.disabled = false;
      button.textContent = originalText;
      return;
    }

    button.textContent = '✓';

    // Dispatch the theme's CartAddEvent so promo bars, gifts, and cart drawer all refresh
    document.dispatchEvent(new CartAddEvent({}, variantId.toString(), {
      source: 'cart-recommendations',
      itemDelta: 1,
      sections: data.sections
    }));

    // Reset button after delay
    setTimeout(() => {
      button.disabled = false;
      button.textContent = originalText;
    }, 1500);

  } catch (e) {
    console.error('Failed to add to cart:', e);
    button.disabled = false;
    button.textContent = originalText;
  }
}

function initializeCartRecommendations() {
  if (window[CART_REC_INIT_FLAG]) return;
  window[CART_REC_INIT_FLAG] = true;

  document.addEventListener('click', handleCartRecommendationClick);

  // Disconnect any stale observer if this script was ever duplicated.
  window[CART_REC_OBSERVER_KEY]?.disconnect?.();

  const loadVisibleRecommendations = () => {
    document.querySelectorAll('[data-cart-recommendations]').forEach((container) => {
      const dialog = container.closest('dialog');
      if (dialog && !dialog.open) return;
      loadCartRecommendations(container);
    });
  };

  // Watch for cart drawer opening and section-rendered cart updates.
  const observer = new MutationObserver(() => {
    loadVisibleRecommendations();
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true });
  window[CART_REC_OBSERVER_KEY] = observer;
  
  loadVisibleRecommendations();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartRecommendations, { once: true });
} else {
  initializeCartRecommendations();
}
